import { useMutation } from '@apollo/client';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import FormControl from 'react-bootstrap/FormControl';
import Row from 'react-bootstrap/Row';
import { GetFramesQuery } from '../../../../../../__gqltypes__';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { UPLOAD_ASSET } from '../../../components/apolloQueries';
import AssetDisplay from './AssetDisplay';
import DragUpload from './DragUpload';
import { Item, OnValidateItem, SetItemData } from './typedef';

type Props = {
  upgrade: GetFramesQuery["liveOps"]["profileFrames"]["list"][0]["nextLevelItems"][0]
  itemData: Item;
  setItemData: SetItemData;
  isNewUpgrade: boolean;
  type: string;
  refetch: () => void;
  onValidate?: OnValidateItem;
};

export default function UpgradeForm({
  upgrade,
  itemData,
  setItemData,
  isNewUpgrade,
  type,
  refetch,
  onValidate,
}: Props) {
  const [uploadAsset, { loading: uploadLoading }] = useMutation(UPLOAD_ASSET, {
    // Merge the uploaded URL into this level so the preview updates immediately; a same-item refetch
    // no longer re-syncs itemData (that would wipe unsaved edits), so we can't rely on it for the preview.
    onCompleted: (d) => {
      // For a leveled upload the new URL is in nextLevelItems (item.asset is the base level), so read it back by level.
      const uploaded = d?.uploadAsset?.item?.nextLevelItems?.find((u: { level: number }) => u.level === upgrade.level);
      const asset = uploaded?.asset;
      if (asset) {
        // @ts-ignore - functional update avoids a stale itemData closure
        setItemData((prev) => ({
          ...prev,
          nextLevelItems: prev.nextLevelItems.map((u) => (u.id === upgrade.id ? { ...u, asset } : u)),
        }));
      }
      refetch();
    },
  });

  const handleUpload = (file: Blob) => {
    uploadAsset({
      variables: {
        input: {
          file,
          id: itemData.id,
          level: upgrade.level,
        },
      },
    });
  };

  // @ts-ignore
  const handleAmountChange = (e) => {
    const nextLevelItems = itemData.nextLevelItems.map((u) =>
      u.id === upgrade.id ? { ...upgrade, cost: { amount: e.target.value } } : u
    );
    // @ts-ignore
    setItemData({ ...itemData, nextLevelItems });
  };
  const isVinyl = itemData.__typename === 'AppSkin';
  const shouldWaitForSaveBeforeUpload = type === 'Frame' && isNewUpgrade;

  return (
    <Form>
      <hr />
      <h5>
        Item level
        {upgrade.level}
      </h5>
      <LoadingLogo show={uploadLoading} />
      {shouldWaitForSaveBeforeUpload ? (
        <>
          <div>Save the item first to unlock asset upload for level {upgrade.level}.</div>
          <br />
        </>
      ) : isNewUpgrade ? (
        <>
          {upgrade.asset ? (
            <Form.Group>
              <Form.Label>Asset</Form.Label>
              <Form.Control value={upgrade.asset} disabled />
              <Form.Text className="text-muted">Auto-filled from the theme upload — click Save to persist this level.</Form.Text>
            </Form.Group>
          ) : (
            <div>To add an asset for the new level, save the item first.</div>
          )}
          <br />
        </>
      ) : (
        <>
          <DragUpload
            onUpload={handleUpload}
            onValidate={onValidate}
            preventSubmit={isVinyl}
            showSelectedFile={isVinyl}
            compact={isVinyl}
          />
          <Form.Group>
            <Form.Label>Asset</Form.Label>
            <Form.Control value={upgrade.asset} disabled />
          </Form.Group>
          {!isVinyl && <AssetDisplay assetUrl={upgrade.asset} />}
        </>
      )}
      <Row>
        <Col>
          <p>Amount: </p>
        </Col>
        <Col md={10}>
          <Form.Group>
            <FormControl value={upgrade.cost?.amount ?? undefined} onChange={handleAmountChange} />
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
}
